import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import PayloftLogo from "../assets/images/payloft-logo.png";
import Migrate from "../assets/images/migrate.png";
import { useAuth } from "../context/AuthContextProvider";
import { MdDashboard, MdOutlinePayments, MdOutlineSettings } from "react-icons/md";
import { LuUsers } from "react-icons/lu";
import { TbReport } from "react-icons/tb";
import { IoIosNotifications } from "react-icons/io";

const NAV_LINKS = [
  {
    to: "/dashboard",
    text: "Dashboard",
    icon: <MdDashboard className="text-2xl" />,
    permission: "View Dashboard",
  },
  {
    to: "/users",
    text: "Users",
    icon: <LuUsers className="text-2xl" />,
    permission: "List Users",
  },
  {
    to: "/transactions",
    text: "Transactions",
    icon: <TbReport className="text-2xl" />,
    permission: "List Transactions",
  },
  {
    to: "/payments/payment-links",
    text: "Payments",
    icon: <MdOutlinePayments className="text-2xl" />,
    permission: "Create Payment Link",
    matchPrefix: "/payments",
  },
  {
    to: "/subscriptions",
    text: "Subscription",
    icon: <TbReport className="text-2xl" />,
    permission: "List Transactions",
  },
  {
    to: "/notification",
    text: "Notification",
    icon: <IoIosNotifications className="text-2xl" />,
  },
  {
    to: "/settlement-report",
    text: "Settlement",
    icon: <TbReport className="text-2xl" />,
    permission: "List Transactions",
  },
  {
    to: "/settings",
    text: "Settings",
    icon: <MdOutlineSettings className="text-2xl" />,
  },
];

const INACTIVE_CLASS = "flex items-center py-4 px-5 gap-3 text-gray-400 hover:text-primary transition-colors";
const ACTIVE_CLASS = "flex items-center py-4 px-5 gap-3 text-primary active-gradient font-medium";

const isPathActive = (pathname, link) => {
  if (link.matchPrefix && pathname.startsWith(link.matchPrefix)) {
    return true;
  }
  return pathname === link.to || pathname.startsWith(`${link.to}/`);
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setIsModalOpen, globalProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const permissions = useMemo(() => user?.permissionList || [], [user?.permissionList]);

  // Filter links based on user permissions
  const filteredLinks = useMemo(() => {
    return NAV_LINKS.filter(
      (link) => !link.permission || permissions.includes(link.permission)
    );
  }, [permissions]);

  // Redirect to first permitted route if user lacks dashboard permission
  useEffect(() => {
    const hasDashboardPermission = permissions.includes("View Dashboard");
    if (!hasDashboardPermission && location.pathname === "/dashboard") {
      const firstLink = filteredLinks.find((link) => link.to !== "/dashboard");
      if (firstLink) {
        navigate(firstLink.to, { replace: true });
      }
    }
  }, [permissions, location.pathname, filteredLinks, navigate]);

  const handleLinkClick = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const showMigrateBanner =
    user?.migrated !== true && globalProfile !== "PRODUCTION";

  return (
    <div className="bg-arena-background min-h-screen font-LT">
      <div className="flex">
        {/* Sidebar container */}
        <aside
          className={`bg-white h-screen md:min-h-screen pt-4 w-[260px] shrink-0 z-50 flex flex-col justify-between transition-transform duration-200 ease-in-out ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 fixed md:relative left-0 top-0 bottom-0 overflow-y-auto md:overflow-y-visible shadow-lg md:shadow-none`}
        >
          <div className="flex flex-col items-center">
            <img
              src={window.__TENANT__?.logo ?? PayloftLogo}
              alt="logo"
              className="w-[92px] h-auto mb-6"
            />

            <nav className="w-[191.98px] flex flex-col gap-1">
              {filteredLinks.map((link) => {
                const active = isPathActive(location.pathname, link);
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={handleLinkClick}
                    style={{ backgroundSize: "25%" }}
                    className={active ? ACTIVE_CLASS : INACTIVE_CLASS}
                  >
                    <span>{link.icon}</span>
                    <span className="text-base font-semibold">{link.text}</span>
                  </NavLink>
                );
              })}

              {showMigrateBanner && (
                <div className="border-t pt-6 mt-4">
                  <button
                    type="button"
                    className="flex items-center py-3 px-5 gap-3 w-full text-left hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                    onClick={() => {
                      setIsModalOpen(true);
                      handleLinkClick();
                    }}
                  >
                    <img src={Migrate} alt="migrate" className="w-6 h-6 shrink-0" />
                    <span className="text-gray-400 text-base font-bold">
                      Migrate to Production
                    </span>
                  </button>
                  <p className="font-extrabold text-[12px] md:text-[14px] text-[#FF0000]/60 mt-4 px-5 leading-snug">
                    Dear users, please note that you are required to provide a
                    Hope PSBank Corporate Account Number to proceed with Go
                    Live. Don&apos;t have an account?{" "}
                    <a
                      href="https://hopepsbank.com/business"
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-solid font-bold hover:text-red-700"
                    >
                      Click Here to create an account.
                    </a>
                  </p>
                </div>
              )}
            </nav>
          </div>
        </aside>

        {/* Mobile backdrop */}
        {isOpen && (
          <div
            className="bg-black/50 fixed inset-0 z-40 md:hidden backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Mobile Hamburger toggle */}
        <div className="md:hidden p-4 fixed top-0 left-0 z-30">
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={isOpen}
            className="text-gray-700 text-2xl p-2 bg-white rounded-md shadow-md focus:outline-none"
          >
            {!isOpen ? "\u2630" : "\u2715"}
          </button>
        </div>

        {/* Main Content Area */}
        <main className="py-5 md:px-6 flex-1 min-w-0 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Sidebar;
